import { Button, Container, Grid, IconButton, Paper, Theme, Tooltip, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import EventList from './EventList';
import { useTranslation } from 'react-i18next';
import { IEvent } from '../../models/IEvent';
import EventServiceInstance from '../../services/EventService';
import TrackdaysCalendarControl from '../common/TrackdaysCalendarControl';
import config from '../../config';
import RefreshIcon from '@mui/icons-material/Refresh';


const drawerWidth = 240;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        toolbar: {
            paddingRight: 24, // keep right padding when drawer closed
        },
        toolbarIcon: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '0 8px',
            ...theme.mixins.toolbar,
        },
        appBar: {
            zIndex: theme.zIndex.drawer + 1,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        },
        menuButton: {
            marginRight: 36,
        },
        menuButtonHidden: {
            display: 'none',
        },
        title: {
            flexGrow: 1,
        },
        drawerPaper: {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        drawerPaperClose: {
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            width: theme.spacing(7),
            [theme.breakpoints.up('sm')]: {
                width: theme.spacing(9),
            },
        },
        appBarSpacer: theme.mixins.toolbar,
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
        fixedHeight: {
            height: 480,
            overflow: 'auto',
        },
    }));

function getEventTime(event: IEvent): number {
    const dateValue: any = (event as any)?.eventDate;
    const time = dateValue ? new Date(dateValue).getTime() : NaN;
    return Number.isFinite(time) ? time : NaN;
}

function compareEventsByDate(a: IEvent, b: IEvent, direction: 'asc' | 'desc' = 'asc'): number {
    const timeA = getEventTime(a);
    const timeB = getEventTime(b);

    const aInvalid = Number.isNaN(timeA);
    const bInvalid = Number.isNaN(timeB);

    if (aInvalid && bInvalid) {
        return 0;
    }
    if (aInvalid) {
        // Push events with invalid dates to the end of the list
        return 1;
    }
    if (bInvalid) {
        // Push events with invalid dates to the end of the list
        return -1;
    }

    return direction === 'asc' ? timeA - timeB : timeB - timeA;
}

function Events() {
    const [events, setEvents] = useState<IEvent[]>();
    const { t } = useTranslation("common");
    const classes = useStyles({});

    const fetchEvents = useCallback(() => {
        const source = axios.CancelToken.source();
        EventServiceInstance.getEvents(undefined, undefined, source.token)
            .then((evts: IEvent[]) => setEvents(evts))
            .catch(e => {
                if (!axios.isCancel(e)) {
                    console.error(e);
                }
            });
        return source;
    }, []);

    useEffect(() => {
        const source = fetchEvents();
        return () => {
            source.cancel();
        };
    }, [fetchEvents]);



    const fixedHeightPaper = clsx(classes.fixedHeight);
    const liveEvents = (events?.filter(e => e.status === 1) ?? []).sort((a, b) =>
        compareEventsByDate(a, b, 'desc')
    );
    const pastEvents = (events?.filter(e => e.status === 2) ?? []).sort((a, b) =>
        compareEventsByDate(a, b, 'desc')
    );
    const upcomingEvents = (events?.filter(e => e.status === 0) ?? []).sort((a, b) =>
        compareEventsByDate(a, b, 'asc')
    );
    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={3}>
                    {/* Refresh button */}
                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title={t("commonLabels.refresh")}>
                            <IconButton
                                onClick={() => fetchEvents()}
                                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                                aria-label={t("commonLabels.refresh") as string}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            onClick={() => fetchEvents()}
                            startIcon={<RefreshIcon />}
                            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                        >{t("commonLabels.refresh")}</Button>
                    </Grid>
                    <Grid item xs={12} md={8} lg={4}>
                        <Typography>{t("events.liveEvents")}</Typography>
                        <Paper className={fixedHeightPaper}>
                            <EventList events={liveEvents} />
                        </Paper>
                    </Grid>
                    {/* Past events */}
                    <Grid item xs={12} md={8} lg={4}>
                        <Typography>{t("events.pastEvents")}</Typography>
                        <Paper className={fixedHeightPaper}>
                            <EventList events={pastEvents} />
                        </Paper>
                    </Grid>
                    {/* Upcoming events */}
                    <Grid item xs={12} md={8} lg={4}>
                        <Typography>{t("events.upcomingEvents")}</Typography>
                        <Paper className={fixedHeightPaper}>
                            <EventList events={upcomingEvents} />
                        </Paper>
                    </Grid>
                    {/* Events calendar */}
                    <Grid item xs={12}>

                        <Paper className={classes.paper}>
                            <TrackdaysCalendarControl eventsFeed={`${config.ontimeApi}/api/events/calendarfeed`} />
                        </Paper>
                    </Grid>
                </Grid>

            </Container>
        </main>);
}
export default Events;